import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ClipboardCheck, UserPlus, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Hero, HeroSkeleton } from "../../components/ui/Hero";
import { adminHeroApi, type AdminHeroStats } from "../../lib/adminHeroApi";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { api, ApiError, type AdminActivity, type AdminPayment } from "../../lib/api";
import { StatusBadge } from "../../components/ui/Badges";
import { AdminEmptyState, AdminPageHeader } from "../../components/admin/AdminUI";
import { useAdminToken, PageError, todayLabel, ACTIVITY_ICONS } from "./adminShared";

export function AdminOverview() {
  const token = useAdminToken();
  const navigate = useNavigate();
  const [hero, setHero] = useState<AdminHeroStats | null>(null);
  const [regChart, setRegChart] = useState<{ day: string; users: number }[]>([]);
  const [recentPayments, setRecentPayments] = useState<AdminPayment[]>([]);
  const [activity, setActivity] = useState<AdminActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    adminHeroApi.stats(token).then(setHero).catch(() => setHero(null));
    api.admin.overview(token)
      .then((data) => {
        setRegChart(data.regChart);
        setRecentPayments(data.recentPayments);
        setActivity(data.activity);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Failed to load overview"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="p-8 space-y-8" aria-busy="true">
        <HeroSkeleton />
        <div className="h-64 rounded-card bg-muted/50 animate-pulse" />
      </div>
    );
  }
  if (error) return <PageError message={error} />;

  return (
    <div className="p-8 space-y-8">
      <Hero
        title="Overview"
        subtitle={todayLabel()}
        stats={[
          { label: "Pending reviews", value: hero?.pendingReviews ?? "-", icon: <ClipboardCheck size={18} />, hint: "Drafts to publish", loading: false, onClick: () => navigate("/admin/courses") },
          { label: "New students", value: hero?.newStudents7d ?? "-", icon: <UserPlus size={18} />, hint: "Last 7 days", onClick: () => navigate("/admin/users") },
          { label: "Premium requests", value: hero?.premiumRequests ?? "-", icon: <Star size={18} />, hint: "Awaiting review", onClick: () => navigate("/admin/payments") },
          { label: "Errors", value: hero?.errors24h ?? "-", icon: <AlertTriangle size={18} />, hint: "Last 24 hours", onClick: () => navigate("/admin/logs") },
        ]}
      />

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 bg-card rounded-sm border border-border p-6 shadow-sm">
          <h3 className="font-bold mb-4">User growth (last 7 days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={regChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="users" stroke="var(--primary)" strokeWidth={2.5} dot={{ r: 4, fill: "var(--primary)" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card rounded-sm border border-border p-6 shadow-sm">
          <h3 className="font-bold mb-4">Quick actions</h3>
          <div className="space-y-2">
            <Link to="/admin/resources" className="block w-full text-left px-4 py-3 rounded-lg border border-border hover:bg-muted transition text-sm font-medium">
              Manage resources
            </Link>
            <Link to="/admin/users" className="block w-full text-left px-4 py-3 rounded-lg border border-border hover:bg-muted transition text-sm font-medium">
              Manage students
            </Link>
            <Link to="/admin/catalog" className="block w-full text-left px-4 py-3 rounded-lg border border-border hover:bg-muted transition text-sm font-medium">
              Universities & courses
            </Link>
            <Link to="/admin/logs" className="block w-full text-left px-4 py-3 rounded-lg border border-border hover:bg-muted transition text-sm font-medium">
              System activity logs
            </Link>
            <Link to="/admin/settings" className="block w-full text-left px-4 py-3 rounded-lg border border-border hover:bg-muted transition text-sm font-medium">
              Platform settings
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h2 className="font-bold">Recent activity</h2>
            <Link to="/admin/logs" className="text-xs text-primary font-semibold">View logs</Link>
          </div>
          <div className="divide-y divide-border">
            {activity.length === 0 ? (
              <p className="px-5 py-8 text-sm text-muted-foreground text-center">No activity yet</p>
            ) : activity.map((a, i) => {
              const conf = ACTIVITY_ICONS[a.type] ?? ACTIVITY_ICONS.register;
              const Icon = conf.icon;
              return (
                <div key={i} className="flex items-center gap-3 px-5 py-3.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${conf.bg}`}>
                    <Icon size={14} className={conf.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{a.user}</p>
                    <p className="text-xs text-muted-foreground">{a.action}</p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{a.time}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border shadow-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h2 className="font-bold">Recent payments</h2>
            <Link to="/admin/payments" className="text-xs text-primary font-semibold">View all</Link>
          </div>
          <div className="divide-y divide-border">
            {recentPayments.length === 0 ? (
              <p className="px-5 py-8 text-sm text-muted-foreground text-center">No payments yet</p>
            ) : recentPayments.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-3.5">
                <div>
                  <p className="text-sm font-semibold">{p.user}</p>
                  <p className="text-xs text-muted-foreground">{p.plan} · {p.date}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold">{p.amount}</p>
                  <StatusBadge status={p.status} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
